import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import SolutionsPageTemplate from "../../components/SolutionsPageTemplate/SolutionsPageTemplate";

const SEO_TITLE = "BPO & Shared Services Productivity Intelligence Software | TeamGrid";
const SEO_DESCRIPTION = "Optimize BPO and shared services with real time workflow visibility, SLA tracking and productivity intelligence for distributed operations.";
const SEO_CANONICAL = "https://teamgrid.ai/solutions/bpo-shared-services";
const OG_IMAGE = "https://teamgrid.ai/og/og-home.webp";

const BPOSharedServices = () => {
  const navigate = useNavigate();
  // FAQ data specific to BPO & Shared Services
  const bpoSharedServicesFAQs = [
    {
      question: "Is TeamGrid suitable for global BPO teams?",
      answer:
        "Yes, TeamGrid provides cross-location productivity insights that help BPO and shared services manage performance consistently.",
    },
    {
      question:
        "Does TeamGrid help with cross-location productivity comparisons?",
      answer:
        "Yes, TeamGrid benchmarks productivity across regions to highlight disparities and best practices.",
    },
    {
      question: "Can TeamGrid improve handoff performance across teams?",
      answer:
        "Yes, it reveals communication delays and handoff inefficiencies that impact team productivity.",
    },
  ];

  const customButton = (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => navigate("/book-demo")}
      className="flex items-center justify-center px-7 py-3.5 rounded-[14px] font-semibold text-black transition-all"
      style={{
        background: "#00D1C1",
        boxShadow: "0px 18px 40px rgba(0, 209, 193, 0.45)",
      }}
    >
      Get a Demo
    </motion.button>
  );

  return (
    <>
      <Helmet>
        <title>{SEO_TITLE}</title>
        <meta name="description" content={SEO_DESCRIPTION} />
        <link rel="canonical" href={SEO_CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SEO_CANONICAL} />
        <meta property="og:title" content={SEO_TITLE} />
        <meta property="og:description" content={SEO_DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="TeamGrid" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={SEO_CANONICAL} />
        <meta name="twitter:title" content={SEO_TITLE} />
        <meta name="twitter:description" content={SEO_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Is TeamGrid suitable for global BPO teams?", acceptedAnswer: { "@type": "Answer", text: "Yes, TeamGrid provides cross-location productivity insights that help BPO and shared services teams manage performance consistently." } },
            { "@type": "Question", name: "Does TeamGrid help with cross-location productivity comparisons?", acceptedAnswer: { "@type": "Answer", text: "Yes, TeamGrid benchmarks productivity across regions to identify performance gaps and best practices." } },
            { "@type": "Question", name: "Can TeamGrid improve handoff performance across teams?", acceptedAnswer: { "@type": "Answer", text: "Yes, TeamGrid reveals communication delays and handoff inefficiencies that impact overall team productivity." } },
          ],
        })}</script>
      </Helmet>
      <SolutionsPageTemplate
        breadcrumbTitle="BPO & Shared Services"
      heroTitle="BPO & Shared Services <span style='color: #00D1C1; font-weight: 600;'>Productivity Intelligence Software</span>"
      heroSubtitle="Optimize Distributed Operations with Consistent Performance Visibility"
      primaryButtonText="Get a Demo for BPO & Shared Services"
      customPrimaryButton={customButton}
      fullSolutionContentTitle="Consistent Quality Across Global Teams"
      fullSolutionContentDescription="TeamGrid helps BPO and shared service teams improve quality, coordination, and operational efficiency at scale through cross-location performance benchmarking, timezone handoff optimization, productivity demonstration, and attrition impact management."
      fullSolutionContentOutcomes="Results include 32% reduction in quality variation, 28% faster cross-timezone completion, 40% improvement in stakeholder satisfaction, and 35% faster recovery from turnover."
      hideProductivity={true}
      hideFeatures={true}
      hideOutcomes={true}
      hideWhyChoose={true}
        faqs={bpoSharedServicesFAQs}
        faqSubtitle="Everything you need to know about TeamGrid for BPO & Shared Services."
      />
    </>
  );
};

export default BPOSharedServices;
